import { useMemo } from 'react';

/**
 * Hook unificado para procesar y calcular todos los datos financieros clave para el dashboard del usuario.
 * Esta es la ÚNICA fuente de verdad para los cálculos financieros en el dashboard.
 * @param {Array} agreements - La lista de acuerdos del usuario.
 * @param {Array} transactions - La lista de transacciones del usuario.
 * @param {object} userProfile - El perfil del usuario para obtener datos como el balance.
 * @returns {object} Un objeto con todos los datos financieros calculados.
 */
const useFinancialSummary = (agreements, transactions, userProfile) => {
  return useMemo(() => {
    // Calcula el ROI total sumando todas las transacciones de tipo 'ganancia_roi'
    const totalROI = transactions
      .filter(tx => tx.type === 'ganancia_roi')
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);

    // CORRECCIÓN: Calcular totalCommissions desde las transacciones para consistencia con Finanzas
    const totalCommissions = transactions
      .filter(tx => ['commission', 'commission_bonus', 'commission_manual_fix', 'commission_level_bonus'].includes(tx.type))
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);

    // --- CORRECCIÓN CRÍTICA (v6): Lógica de deducciones restaurada y corregida según info.txt ---
    // Se incluyen solo las transacciones que representan un egreso REAL del balance retirable.
    // El 'fee_acelerador' inicial se descuenta del capital invertido, NO del balance retirable.
    const totalDeductions = transactions
      .filter(tx => [
        // Retiros de balance (confirmados por el usuario)
        'Retiro',
        'retiro_aprobado',
        'fee_retiro', // Comisión del 10% por retiro

        // Otras deducciones operativas que afectan el balance retirable
        'reversion_ganancia',       // Reversión de una ganancia
        'fee_reactivacion_acelerador', // Fee por reactivar un acelerador (confirmado como deducción)
        'fee_pequeno_dueno',           // Fee mensual por ser "Pequeño Dueño"
        
        // Casos especiales
        'hold_retiro', // Retención temporal para retiros en proceso
      ].includes(tx.type) || (tx.type === 'ajuste_manual' && tx.amount < 0))
      .reduce((sum, tx) => sum + Math.abs(tx.amount || 0), 0);

    // Calcula el capital activo sumando el valor neto de los acuerdos aprobados
    const activeCapital = (agreements || [])
      .filter(a => a.status === 'aprobado')
      .reduce((sum, a) => sum + (a.netInvestmentAmount || a.amount || 0), 0);

    // --- CORRECCIÓN CRÍTICA (v6): Lógica de ingresos restaurada y corregida ---
    // Se suman todas las transacciones que representan un ingreso al balance.
    const totalAdjustments = transactions
      .filter(tx => 
        (tx.type === 'ajuste_manual' && tx.amount > 0) || // Solo ajustes manuales positivos
        ['ajuste_correccion_negativo', 'reembolso_retiro'].includes(tx.type))
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);

    // La ganancia bruta es la suma de ROI, comisiones y cualquier otro ajuste.
    const grossEarnings = totalROI + totalCommissions + totalAdjustments;

    const todaysEarnings = transactions
      .filter(tx => tx.type === 'ganancia_roi' && tx.createdAt?.toDate() >= new Date().setHours(0, 0, 0, 0))
      .reduce((sum, tx) => sum + tx.amount, 0);

    const activeAgreements = agreements.filter(a => a.status === 'aprobado');

    // Capital devuelto al saldo al cancelar un acuerdo. No es ganancia (no va en grossEarnings),
    // pero el backend sí lo acredita en withdrawableBalance.
    const totalRefunds = transactions
      .filter(tx => tx.type === 'cancelacion_acuerdo')
      .reduce((sum, tx) => sum + (tx.amount || 0), 0);

    // --- CÁLCULO FINAL DE BALANCE RETIRABLE ---
    // La fuente de verdad es withdrawableBalance del perfil: es el saldo con el que el backend valida
    // los retiros. Recalcularlo desde el historial dejaba afuera movimientos (ej. cancelaciones) y
    // mostraba saldos negativos que no existían (ver BITACORA.md, 2026-09-29). El recálculo queda solo
    // como respaldo mientras carga el perfil. Nunca se muestra negativo: si no hay plata, es $0.
    const profileBalance = Number(userProfile?.withdrawableBalance);
    const rawWithdrawableBalance = Number.isFinite(profileBalance)
      ? profileBalance
      : grossEarnings + totalRefunds - totalDeductions;
    const calculatedWithdrawableBalance = Math.max(0, Math.round(rawWithdrawableBalance * 100) / 100);

    // --- CORRECCIÓN: Devolver la estructura de datos unificada que el Dashboard espera ---
    return {
        kpiData: {
            withdrawableBalance: calculatedWithdrawableBalance,
            todaysEarnings,
            activeCapital,
            teamSize: userProfile?.teamSize || 0,
        },
        financialSummary: {
            totalROI,
            totalCommissions,
            totalDeductions,
            totalRefunds,
            grossEarnings,
            withdrawableBalance: calculatedWithdrawableBalance,
        },
        chartData: {
            labels: ['Normal', 'Acelerado'],
            datasets: [{
                data: [
                    activeAgreements.filter(a => !a.acceleratorActive).reduce((s, a) => s + (a.netInvestmentAmount || a.amount), 0),
                    activeAgreements.filter(a => a.acceleratorActive).reduce((s, a) => s + (a.netInvestmentAmount || a.amount), 0)
                ]
            }]
        },
    };
  }, [agreements, transactions, userProfile]);
};

export default useFinancialSummary;
